import { _defineProperty } from "./index13.js";
import { Side } from "./index50.js";
import { mainLoadOptions } from "./index53.js";
import { ThreadData } from "./index54.js";
import { ThreadCollider } from "./index55.js";
import { Vectors, randomWeightedItem } from "@project-rouge/rg-three";
import { Line3, MathUtils, Vector3 } from "three";
var TOL = 1e-5;
var _cross = new Vector3();
var _sideDirection = new Vector3();
var ThreadController = class ThreadController {
	constructor(props) {
		_defineProperty(this, "collider", void 0);
		_defineProperty(this, "data", void 0);
		_defineProperty(this, "context", void 0);
		this.collider = new ThreadCollider(props);
		this.data = this.collider.data;
		this.context = this.data.context;
	}
	static createMain({ context, boundary, holes, start, dir, loadOptions = mainLoadOptions.dynamic, plotIndex, tryTrimStart }) {
		if (!start || !dir) {
			for (const load of loadOptions) {
				const thread$1 = ThreadController.createMainFromBoundary({
					boundary,
					holes,
					context,
					load,
					plotIndex,
					tryTrimStart
				});
				if (thread$1) return thread$1;
			}
			return;
		}
		const thread = new ThreadController({
			load: void 0,
			bidirectional: true,
			start,
			dir,
			context,
			boundary,
			holes,
			plotIndex
		});
		if (!thread.isValidStart()) return;
		return thread.grow();
	}
	static createMainFromBoundary({ boundary, holes, load, context, plotIndex, tryTrimStart }) {
		const { unboundWeightMod, rng, edgeWeightMods } = context;
		const lines = boundaryToLines(boundary);
		const ogLines = lines.slice();
		let partialProps;
		const unboundedWeights = !unboundWeightMod ? void 0 : getWeights({
			lines,
			edgeWeightMods: [unboundWeightMod]
		});
		while (lines.length > 0) {
			const line = popRandomLine({
				lines,
				rng,
				edgeWeightMods
			});
			if (!line) return;
			partialProps = getStartFromLine(line, context, load);
			if (!partialProps) continue;
			if (unboundedWeights) tryTrimStart = shouldTrimAtStart(ogLines.indexOf(line), unboundedWeights, tryTrimStart);
			const thread = new ThreadController({
				load,
				bidirectional: true,
				start: partialProps.start,
				dir: partialProps.dir,
				context,
				boundary,
				holes,
				plotIndex,
				tryTrimStart
			});
			if (thread.isValidStart()) return thread.grow();
		}
	}
	add({ start, right, load }) {
		if (!this.canAdd({
			start,
			right,
			load
		})) return;
		this.data.shrinkBranchToBranch();
		const point = (start ? this.data.start : this.data.end).clone();
		const dir = start ? this.data.dir : this.data.nDir;
		const depths = ThreadData.getDepths(this.context, load);
		const moveBack = start === right ? depths.right : depths.left;
		const moveRight = right ? this.data.depthRight : -this.data.depthLeft;
		point.addScaledVector(dir, moveBack);
		point.addScaledVector(this.data.right, moveRight);
		const threadDir = this.data.right.clone();
		if (!right) threadDir.negate();
		const thread = new ThreadController({
			start: point,
			dir: threadDir,
			boundary: this.data.boundary,
			holes: this.data.holes,
			load,
			context: this.context,
			plotIndex: this.data.plotIndex
		});
		thread.data.startThread = {
			thread: this.data,
			right: start ? !right : right
		};
		const branch = thread.grow();
		if (branch) return branch;
		this.data.unshrinkBranchToBranch();
	}
	save({ thread, start }) {
		this.data.save({
			data: thread.data,
			start
		});
	}
	grow() {
		if (this.data.bidirectional) this._grow({ forward: false });
		this._grow({ forward: true });
		if (!this.isValidGrowth()) return;
		return this;
	}
	_grow({ forward }) {
		const size = this.collider.getGrowLimit(forward) - TOL;
		const point = forward ? this.data.end : this.data.start;
		const dir = forward ? this.data.dir : this.data.nDir;
		point.addScaledVector(dir, size);
		this.data.updateSize();
		this.collider.trimToMaxWingLength();
	}
	isValidGrowth() {
		if (this.data.isMain) return this.data.size >= this.context.minLineBuilding;
		return this.data.size >= this.context.minBranchLength;
	}
	isValidStart() {
		if (!this.passNorthFacingCheck({
			right: this.data.right,
			load: this.data.load
		})) return false;
		if (this.collider.isInsideHole()) return false;
		if (!this.collider.isInsideBoundary()) return false;
		return true;
	}
	canAdd({ start, right, load }) {
		const newRight = right ? this.data.nDir : this.data.dir;
		if (!this.passNorthFacingCheck({
			right: newRight,
			load
		})) return false;
		if (!this.passTBranchCheck(start)) return false;
		if (!this.passFace2FaceCheck({
			start,
			right,
			load
		})) return false;
		if (!this.passMainMinSizeCheck()) return false;
		if (!this.passCanTurnCheck()) return false;
		if (!this.passGrowEnds(start)) return false;
		if (!this) return false;
		return true;
	}
	passNorthFacingCheck({ right, load }) {
		if (this.context.avoidNorthFactor < 1) return true;
		_sideDirection.copy(right);
		if (load === Side.left) _sideDirection.negate();
		const avoidNorthRange = this.context.avoidNorthRange;
		const dotRange = Math.cos(.5 * avoidNorthRange * MathUtils.DEG2RAD);
		const north = Vectors.Back;
		const dot = _sideDirection.dot(north);
		if (dot >= dotRange) return false;
		if (load === void 0 && Math.abs(dot) >= dotRange) return false;
		return true;
	}
	passCanTurnCheck() {
		return this.data.size >= this.context.canTurnMinSize;
	}
	passGrowEnds(start) {
		if (!this.data.bidirectional && start) return false;
		return true;
	}
	passTBranchCheck(start) {
		if (start && this.data.startThread) return false;
		if (!start && this.data.endThread) return false;
		return true;
	}
	passMainMinSizeCheck() {
		if (this.data.startThread || this.data.endThread) return this.context.min2CornerWing <= this.data.size;
		return this.context.min1CornerWing <= this.data.size;
	}
	passFace2FaceCheck({ start, right, load }) {
		const oppositeThread = start ? this.data.endThread : this.data.startThread;
		if (!oppositeThread) return true;
		if (oppositeThread.right !== right) return true;
		const depths = ThreadData.getDepths(this.context, load);
		let f2f;
		if (this.data.bidirectional) f2f = this.data.size + 2 * TOL - oppositeThread.thread.depth - depths.left - depths.right;
		else f2f = this.data.size + 2 * TOL - depths.left - depths.right;
		const f2fRequired = Math.max(this.context.face2face, this.context.minApartmentWidth);
		return f2f >= f2fRequired;
	}
};
function getStartFromLine(line, context, load) {
	const start = line.getCenter(new Vector3());
	const dir = line.delta(new Vector3()).normalize();
	_cross.crossVectors(dir, Vectors.Up);
	let offset = TOL;
	if (load === Side.right) offset += context.singleSmallSide;
	else offset += context.singleBigSide;
	start.addScaledVector(_cross, offset);
	return {
		start,
		dir,
		load
	};
}
function popRandomLine({ lines, rng, edgeWeightMods }) {
	const list = lines.map((_, i$1) => i$1);
	const weights = getWeights({
		lines,
		edgeWeightMods
	});
	if (weights.reduce((sum, w) => sum + w, 0) === 0) {
		lines.length = 0;
		return;
	}
	let i = 0;
	if (rng) i = randomWeightedItem({
		list,
		weights,
		rng
	});
	else {
		const bestWeight = Math.max(...weights);
		i = weights.indexOf(bestWeight);
	}
	const line = lines[i];
	lines.splice(i, 1);
	return line;
}
function shouldTrimAtStart(i, unboundedWeights, defaultValue) {
	const unboundedNeighbours = [unboundedWeights[(unboundedWeights.length + i - 1) % unboundedWeights.length], unboundedWeights[(i + 1) % unboundedWeights.length]];
	if (unboundedNeighbours[0] === unboundedNeighbours[1]) return defaultValue;
	return unboundedNeighbours[0] < unboundedNeighbours[1];
}
function getWeights({ lines, edgeWeightMods }) {
	const weights = new Array(lines.length).fill(1);
	for (const mod of edgeWeightMods) {
		const modWeights = mod(lines);
		for (let i = 0; i < lines.length; i++) weights[i] *= modWeights[i];
	}
	return weights;
}
function boundaryToLines(boundary) {
	const lines = [];
	for (let i = 1; i < boundary.length; i++) {
		const line = new Line3(boundary[i - 1].clone(), boundary[i].clone());
		lines.push(line);
	}
	return lines;
}
export { ThreadController };

//# sourceMappingURL=index56.js.map